import { useCallback, useEffect, useMemo, useState } from 'react'

/**
 * 북마크 다중 선택 상태.
 * 페이지 이동·필터 변경으로 화면에서 사라진 항목은 선택에서 자동으로 빠진다
 * (보이지 않는 링크가 일괄 삭제에 휩쓸리는 것을 막는다).
 */

export interface UseBookmarkSelectionResult {
  selectedIds: string[]
  isSelectionMode: boolean
  isAllSelected: boolean
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  selectAll: () => void
  clear: () => void
}

export function useBookmarkSelection(
  visibleIds: string[]
): UseBookmarkSelectionResult {
  const [selected, setSelected] = useState<string[]>([])

  const visibleKey = visibleIds.join(',')

  useEffect(() => {
    setSelected(previous => {
      const visible = new Set(visibleIds)
      const next = previous.filter(id => visible.has(id))
      return next.length === previous.length ? previous : next
    })
    // 화면에 보이는 항목 목록이 바뀔 때만 정리한다
  }, [visibleKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const isAllSelected = useMemo(
    () =>
      visibleIds.length > 0 && visibleIds.every(id => selected.includes(id)),
    [visibleIds, selected]
  )

  const toggle = useCallback((id: string) => {
    setSelected(previous =>
      previous.includes(id)
        ? previous.filter(item => item !== id)
        : [...previous, id]
    )
  }, [])

  const selectAll = useCallback(() => {
    setSelected(previous =>
      visibleIds.length > 0 && visibleIds.every(id => previous.includes(id))
        ? []
        : [...visibleIds]
    )
  }, [visibleIds])

  const clear = useCallback(() => setSelected([]), [])

  const isSelected = useCallback(
    (id: string) => selected.includes(id),
    [selected]
  )

  return {
    selectedIds: selected,
    isSelectionMode: selected.length > 0,
    isAllSelected,
    isSelected,
    toggle,
    selectAll,
    clear,
  }
}

export default useBookmarkSelection
